// ---------------------------------------------------------------------------
// GaugeChart -- Semicircle Gauge with Capsule Arc
// ---------------------------------------------------------------------------
// Renders a semicircular gauge using SVG path with:
//   - stroke-linecap="round" for capsule arc ends
//   - Color gradient: red (low) -> amber (mid) -> green (high)
//   - Center metric with font-display for hero numbers
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react';
import { ChartCard } from './ChartCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GaugeChartProps {
  /** Current value */
  readonly value: number;
  /** Maximum value (denominator) */
  readonly maxValue: number;
  /** Card title */
  readonly title: string;
  /** Optional card subtitle */
  readonly subtitle?: string;
  /** Label text below the value */
  readonly label?: string;
  /** SVG width */
  readonly size?: number;
  /** Additional CSS class */
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// SVG constants
// ---------------------------------------------------------------------------

const STROKE_WIDTH = 16;
const TRACK_OPACITY = 0.4; // Increased from 0.15 → 0.25 → 0.4 for better dark mode visibility

// ---------------------------------------------------------------------------
// Color thresholds (red/amber/green based on value/max ratio)
// ---------------------------------------------------------------------------

function getGaugeColor(ratio: number): string {
  if (ratio < 0.33) return 'hsl(var(--felton-chart-4))'; // Rose (low)
  if (ratio < 0.66) return 'hsl(var(--felton-chart-3))'; // Amber (mid)
  return 'hsl(var(--felton-chart-6))'; // Emerald (high)
}

// ---------------------------------------------------------------------------
// Arc path generator for semicircle (180 degrees)
// ---------------------------------------------------------------------------

function describeArc(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string {
  const toRad = (deg: number) => ((deg - 90) * Math.PI) / 180;
  const start = {
    x: cx + radius * Math.cos(toRad(endAngle)),
    y: cy + radius * Math.sin(toRad(endAngle)),
  };
  const end = {
    x: cx + radius * Math.cos(toRad(startAngle)),
    y: cy + radius * Math.sin(toRad(startAngle)),
  };
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  maxValue,
  title,
  subtitle,
  label,
  size = 170,
  className,
}) => {
  const safeMax = maxValue > 0 ? maxValue : 1;
  const ratio = Math.max(0, Math.min(1, value / safeMax));
  const color = useMemo(() => getGaugeColor(ratio), [ratio]);

  const radius = (size - STROKE_WIDTH) / 2;
  const cx = size / 2;
  // Position cy at the bottom of the viewBox so the semicircle curves upward
  const svgHeight = radius + STROKE_WIDTH;
  const cy = svgHeight - STROKE_WIDTH / 2;

  // Semicircle: from left (-90°) to right (90°), curving through the top
  // Using -90 to 90 creates a proper gauge arc from west to east via north
  const trackPath = describeArc(cx, cy, radius, -90, 90);
  const valueEndAngle = -90 + ratio * 180; // From -90 (left) toward 90 (right)
  const valuePath = valueEndAngle > -89.5 ? describeArc(cx, cy, radius, -90, valueEndAngle) : '';

  // Display value
  const displayValue = Number.isInteger(value) ? value.toString() : value.toFixed(1);

  // Text position: center of the semicircle area
  const textY = cy - radius / 3;

  return (
    <ChartCard title={title} subtitle={subtitle} className={className}>
      {/* min-h-[160px] ensures consistent height with DonutChart and other chart types */}
      <div className="flex flex-col items-center justify-center min-h-[160px]">
        <svg
          width={size}
          height={svgHeight}
          viewBox={`0 0 ${size} ${svgHeight}`}
          className="overflow-visible"
        >
          {/* Track outer glow (subtle depth) */}
          <path
            d={trackPath}
            fill="none"
            stroke="hsl(var(--v11-border))"
            strokeWidth={STROKE_WIDTH + 2}
            strokeLinecap="round"
            opacity={0.15}
          />

          {/* Track (background arc) */}
          <path
            data-testid="gauge-track"
            d={trackPath}
            fill="none"
            stroke="hsl(var(--v11-border))"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            opacity={TRACK_OPACITY}
          />

          {/* Value arc */}
          {valuePath && (
            <path
              data-testid="gauge-fill"
              d={valuePath}
              fill="none"
              stroke={color}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
            />
          )}

          {/* Center metric */}
          <text
            x={cx}
            y={textY}
            textAnchor="middle"
            dominantBaseline="central"
            className="fill-v11-text-primary font-display text-2xl font-bold"
          >
            {displayValue}
          </text>

          {/* Scale labels: 0 (left) and max (right) */}
          <text
            x={cx - radius - 2}
            y={cy + 14}
            textAnchor="middle"
            className="fill-v11-text-muted text-[9px]"
          >
            0
          </text>
          <text
            x={cx + radius + 2}
            y={cy + 14}
            textAnchor="middle"
            className="fill-v11-text-muted text-[9px]"
          >
            {maxValue}
          </text>
        </svg>

        {/* Label below gauge */}
        {label && <span className="mt-1 text-xs text-v11-text-muted">{label}</span>}
      </div>
    </ChartCard>
  );
};
